import React, { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { MdCalendarMonth, MdCheckCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Paper, Typography, CircularProgress } from '@mui/material';
import { signInWithGoogleCalendar } from '../utils/firebase.utils';
import { connectGoogleCalendar, getBusinessProfile } from '../logic/business/action';

const GoogleCalendarSettings = ({ compact = false }) => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const [connecting, setConnecting] = useState(false);

  const isConnected = Boolean(
    selector?.details?.access_token || selector?.details?.google_headers
  );

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const googleData = await signInWithGoogleCalendar();
      if (!googleData?.access_token) return;
      await connectGoogleCalendar(dispatch, googleData);
      await getBusinessProfile(dispatch);
    } catch (error) {
      console.error(error);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Paper
      sx={{
        p: compact ? 2 : 3,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: '#f0f4ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MdCalendarMonth size={28} color="#4285F4" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Google Calendar & Meet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Google account to auto-create Calendar events and generate Meet links for virtual appointments.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {isConnected ? (
              <>
                <MdCheckCircle color="#10b981" />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                  Connected
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                Not connected — virtual appointments will not get scheduled Meet links
              </Typography>
            )}
          </Box>

          <Button
            variant={isConnected ? 'outlined' : 'contained'}
            startIcon={connecting ? <CircularProgress size={18} color="inherit" /> : <FcGoogle />}
            onClick={handleConnect}
            disabled={connecting}
            sx={{ textTransform: 'none' }}
          >
            {connecting
              ? 'Connecting...'
              : isConnected
              ? 'Reconnect Google Account'
              : 'Connect Google Calendar'}
          </Button>

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Requires Google Cloud OAuth with Calendar API enabled. Scopes: calendar.events
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default GoogleCalendarSettings;
